import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    createAbsoluteFromTokens,
    isModelPath,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { arrayIsNotEmpty, isNotNullOrUndefined, reject } from "../../../../../Common/TypingUtils";
import { Deferred } from "../../../Helpers/Deferred";
import { ModelHunk } from "../ModelStoring/ModelHunk";
import { PathLoadSpecification } from "../../../../../Common/CandyApiClient/ICandyApiClient";
import { StringArrayKey } from "../../../../../Common/StringArrayKey";
import { Map2 } from "../../../../../Common/ModelPath/Map/Map2";
import { buildIterationSequenceWithInstances } from "../../../../../Common/ModelPath/BuildIterationSequence";
import { IterUtils, reverseArray } from "../../../../../Common/IterableUtils";
import { IReadonlyModelHunk } from "../ModelStoring/IReadonlyModelHunk";
import { BindingPath, ViewModel } from "../CoreTypes";
import { getMaskWithInstances } from "../../../../../Common/ModelPath/ModelPathHelper";
import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";

import { LazyLoadingDeclaration, LazyLoadingNodeDeclaration } from "./LazyLoadingDeclaration";

export enum LazyLoadingInstanceState {
    OnlyInitialData,
    OnlyRequiredData,
    FullyLoaded,
    Pending,
}

export interface LazyLoadRule {
    modelPath: AbsoluteModelPath | AbsoluteModelFieldPath;
    skip: boolean;
}

export type InstancesExtractedFromPath = string[];
export type LazyLoadedPathResolveResult = InstancesExtractedFromPath[];

export interface ILazyLoadingManager {
    requestInstancesLoad(multiplicityPath: AbsoluteModelPath, ...instances: string[]): Promise<void>;
    requestFullInstanceLoad(multiplicityPath: AbsoluteModelPath, ...instances: string[]): Promise<void>;
    requestFullInstanceLoadForCustomPath(multiplicityPath: AbsoluteModelPath): Promise<void>;
    getInstanceLazyLoadingState(multiplicityPath: AbsoluteModelPath, instance: string): LazyLoadingInstanceState;
    getLazyLoadingStateByPath(path: AbsoluteModelPath): LazyLoadingInstanceState;
    resolvePathByLoadedInstances(path: AbsoluteModelPath | AbsoluteModelFieldPath): LazyLoadedPathResolveResult;
    resolvePathByInitialRules(sourcePath: AbsoluteModelPath | AbsoluteModelFieldPath): LazyLoadedPathResolveResult;

    getServerOnlyRules(): Set<LazyLoadRule>;
    getInitialLazyLoadRules(): Iterable<LazyLoadRule>;
    hasLazyLoadRules(): boolean;
    isLazyLoadingEnabled(): boolean;
    setLazyLoadingEnabledMode(enabled: boolean): void;
    clearState(): Promise<void>;
    waitForAllPendingRequests(): Promise<void>;
}

interface LazyLoadingElementInstanceInfo {
    state: LazyLoadingInstanceState;
    loadingDeferred?: Deferred<void>;
}

interface LazyLoadingElementInfo {
    instanceIds: Map2<StringArrayKey, LazyLoadingElementInstanceInfo>;
}

export interface IServerDataProvider {
    loadInner(paths: PathLoadSpecification[]): Promise<ModelHunk>;
}

export interface IUpdatedDataTarget {
    apply(data: IReadonlyModelHunk): void;
}

export interface ICurrentStoreDataProvider {
    sendCurrentChanges(): Promise<void>;
    getViewModel(path: BindingPath | AbsoluteResolvedModelPath): ViewModel | undefined;
}

export class LazyLoadingManager implements ILazyLoadingManager {
    private readonly originalLazyLoadingDeclaration: Map2<AbsoluteModelFieldPath, LazyLoadingNodeDeclaration>;
    private readonly lazyLoadingElementInfo: Map2<AbsoluteModelPath, LazyLoadingElementInfo> = new Map2<
        AbsoluteModelPath,
        LazyLoadingElementInfo
    >();
    private readonly serverDataProvider: IServerDataProvider;
    private readonly updatedDataTarget: IUpdatedDataTarget;
    private readonly currentStoreDataProvider: ICurrentStoreDataProvider;

    private lazyLoadingEnabled: boolean = false;
    private isInCleaningState: boolean = false;

    public constructor(
        lazyLoadingDeclaration: LazyLoadingDeclaration,
        serverDataProvider: IServerDataProvider,
        updatedDataTarget: IUpdatedDataTarget,
        currentStoreDataProvider: ICurrentStoreDataProvider
    ) {
        this.currentStoreDataProvider = currentStoreDataProvider;
        this.updatedDataTarget = updatedDataTarget;
        this.serverDataProvider = serverDataProvider;
        this.originalLazyLoadingDeclaration = Object.entries(lazyLoadingDeclaration).reduce(
            (acc, [bindingPath, declarationNode]) => {
                const pathWithField = AbsoluteModelFieldPath.createFromMask(bindingPath, PathTokens.each, "");
                acc.set(pathWithField, declarationNode);
                return acc;
            },
            new Map2<AbsoluteModelFieldPath, LazyLoadingNodeDeclaration>()
        );
    }

    public resolvePathByInitialRules(
        sourcePath: AbsoluteModelPath | AbsoluteModelFieldPath
    ): LazyLoadedPathResolveResult {
        const path = isModelPath(sourcePath) ? sourcePath : sourcePath.path;
        const maskWithInstances = getMaskWithInstances(path);

        const isInitialRule =
            sourcePath instanceof AbsoluteModelFieldPath &&
            this.originalLazyLoadingDeclaration.get(
                new AbsoluteModelFieldPath(maskWithInstances.mask, sourcePath.field)
            )?.lazyLoadMode === "initial";

        if (isInitialRule) {
            const firstDeclaration = this.getFirstLazyDeclaration(path);
            const concreteInstances = maskWithInstances.instances.filter(PathTokens.isInstanceToken);
            if (
                isInitialRule &&
                firstDeclaration != undefined &&
                firstDeclaration.iterable.getIterationRank() >= concreteInstances.length
            ) {
                return [concreteInstances];
            }
        }
        return [];
    }

    public resolvePathByLoadedInstances(
        sourcePath: AbsoluteModelPath | AbsoluteModelFieldPath
    ): LazyLoadedPathResolveResult {
        const path = isModelPath(sourcePath) ? sourcePath : sourcePath.path;
        const maskWithInstances = getMaskWithInstances(path);
        const firstDeclaration = this.getFirstLazyDeclaration(path);
        const concreteInstances = maskWithInstances.instances.filter(PathTokens.isInstanceToken);
        if (firstDeclaration == undefined || !this.lazyLoadingEnabled) {
            return [concreteInstances];
        }

        const multiElementInfo = this.getLazyLoadingElementInfo(firstDeclaration.iterable);

        const result = Iterator.from(multiElementInfo.instanceIds.keys())
            .filter(key => {
                for (const [index, instance] of IterUtils.iterateEntries(concreteInstances)) {
                    if (index >= key.items.length) {
                        return true;
                    }
                    if (key.items[index] !== instance) {
                        return false;
                    }
                }
                return true;
            })
            .map(x => x.items.concat(concreteInstances.slice(x.items.length)))
            .toArray();
        return result;
    }

    public getInstanceLazyLoadingState(
        multiplicityPath: AbsoluteModelPath,
        instance: string
    ): LazyLoadingInstanceState {
        const path = multiplicityPath.joinWith(instance);
        return this.getLazyLoadingStateByPath(path);
    }

    public getLazyLoadingStateByPath(path: AbsoluteModelPath): LazyLoadingInstanceState {
        const firstDeclaration = this.getFirstLazyDeclaration(path);

        if (firstDeclaration != undefined && firstDeclaration.lazyLoadMode == "deferred") {
            const multiElementInfo = this.getLazyLoadingElementInfo(firstDeclaration.iterable);
            const key = new StringArrayKey(
                firstDeclaration.instances.map(x => (PathTokens.isInstanceToken(x) ? x : "*"))
            );
            const elementInstanceData = multiElementInfo.instanceIds.get(key);
            if (elementInstanceData != undefined) {
                return elementInstanceData.state;
            }
        }

        return firstDeclaration?.lazyLoadMode === "deferred"
            ? LazyLoadingInstanceState.OnlyInitialData
            : LazyLoadingInstanceState.FullyLoaded;
    }

    public async requestInstancesLoad(multiplicityPath: AbsoluteModelPath, ...instances: string[]): Promise<void> {
        // eslint-disable-next-line no-warning-comments
        // TODO: Временный костыль! Грузим сразу весь инстанс несмотря на то, есть у него required поля
        return await this.requestInstancesLoadInternal(
            LazyLoadingInstanceState.FullyLoaded,
            multiplicityPath,
            ...instances
        );
    }

    private getFirstLazyDeclaration(path: AbsoluteModelPath) {
        const iterationSequences = buildIterationSequenceWithInstances(path);
        const revertedIterationSequences = reverseArray(iterationSequences);
        const firstDeclaration = revertedIterationSequences
            .map(x => ({
                lazyLoadMode: this.originalLazyLoadingDeclaration.get(
                    new AbsoluteModelFieldPath(x.iterable.joinWith(PathTokens.each), "")
                )?.lazyLoadMode,
                iterable: x.iterable,
                instances: x.instances,
            }))
            .find(x => isNotNullOrUndefined(x.lazyLoadMode) && x.lazyLoadMode != "request");
        return firstDeclaration;
    }

    private async requestInstancesLoadInternal(
        requestLoadingMode: LazyLoadingInstanceState,
        multiplicityPath: AbsoluteModelPath,
        ...instances: string[]
    ): Promise<void> {
        if (!this.lazyLoadingEnabled || this.isInCleaningState) {
            return;
        }

        const firstDeclaration = this.getFirstLazyDeclaration(multiplicityPath.joinWith(PathTokens.each));
        if (firstDeclaration != undefined && firstDeclaration.lazyLoadMode == "deferred") {
            if (
                firstDeclaration.iterable.getPathPartsAsArray().length < multiplicityPath.getPathPartsAsArray().length
            ) {
                return;
            }

            const prevInstances = firstDeclaration.instances
                .slice(0, firstDeclaration.instances.length - 1)
                .map(x => (PathTokens.isInstanceToken(x) ? x : reject()));
            const multiElementInfo = this.getLazyLoadingElementInfo(firstDeclaration.iterable);
            const instanceKeys = instances.map(instance => new StringArrayKey([...prevInstances, instance]));
            const instanceInfos = instanceKeys.map(key => {
                const instanceInfo = multiElementInfo.instanceIds.get(key);
                return instanceInfo;
            });
            const instancesInPendingStateTasks = instanceInfos
                .map(x => x?.loadingDeferred)
                .filter(isNotNullOrUndefined)
                .map(x => x.promise);

            const deferred = new Deferred<void>();
            const requiredInstanceInfos = instanceKeys
                .map(key => {
                    const instanceInfo = multiElementInfo.instanceIds.getOrSet(key, () => ({
                        state: LazyLoadingInstanceState.Pending,
                        loadingDeferred: deferred,
                    }));
                    return {
                        instanceInfo: instanceInfo,
                        key: key,
                    };
                })
                .filter(
                    x =>
                        x.instanceInfo.state === LazyLoadingInstanceState.Pending &&
                        x.instanceInfo.loadingDeferred === deferred
                );
            const requiredInstances = requiredInstanceInfos.map(x => IterUtils.last(x.key.items));
            const requestInstancesPathPart =
                requiredInstances.length > 0 ? `[${requiredInstances.join(",")}]` : undefined;

            const pathToLoad: PathLoadSpecification | undefined =
                requestInstancesPathPart != undefined
                    ? {
                          pathOrPathPrefix: multiplicityPath.joinWith(requestInstancesPathPart),
                          skip: false,
                      }
                    : undefined;

            const paths: PathLoadSpecification[] = pathToLoad != undefined ? [pathToLoad] : [];

            try {
                if (arrayIsNotEmpty(instancesInPendingStateTasks)) {
                    await Promise.all(instancesInPendingStateTasks);
                }
                if (arrayIsNotEmpty(paths)) {
                    const data = await this.loadDataWithChangesCheck(paths);
                    this.updatedDataTarget.apply(data);
                }
            } finally {
                for (const requiredInstanceInfo of requiredInstanceInfos) {
                    requiredInstanceInfo.instanceInfo.state = requestLoadingMode;
                    requiredInstanceInfo.instanceInfo.loadingDeferred = undefined;
                }
                deferred.resolve();
            }
        }
    }

    private getLazyLoadingElementInfo(multiplicityPath: AbsoluteModelPath) {
        return this.lazyLoadingElementInfo.getOrSet(multiplicityPath, () => ({
            instanceIds: new Map2<StringArrayKey, LazyLoadingElementInstanceInfo>(),
        }));
    }

    private async loadDataWithChangesCheck(paths: PathLoadSpecification[]): Promise<ModelHunk> {
        await this.currentStoreDataProvider.sendCurrentChanges();
        const data = await this.serverDataProvider.loadInner(paths);
        for (const [path, viewModel] of [...data.iterateEntries()]) {
            const currentViewModel = this.currentStoreDataProvider.getViewModel(path) ?? {};
            data.set(path, { ...viewModel, ...currentViewModel });
        }
        return data;
    }

    public getServerOnlyRules(): Set<LazyLoadRule> {
        const serverOnlyRules = Iterator.from(this.originalLazyLoadingDeclaration.entries())
            .map(x => ({ path: x[0], declaration: x[1] }))
            .filter(x => x.declaration?.lazyLoadMode === "serverOnly")
            .map(x => ({
                modelPath: x.path,
                skip: true,
            }));
        return new Set(serverOnlyRules);
    }

    public *getInitialLazyLoadRules(): Iterable<LazyLoadRule> {
        yield* Iterator.from(this.originalLazyLoadingDeclaration.entries())
            .map(x => ({ path: x[0], declaration: x[1] }))
            .filter(x => x.declaration?.lazyLoadMode === "initial" || x.declaration?.lazyLoadMode === "deferred")
            .map(x => ({
                modelPath: x.path,
                skip: x.declaration?.lazyLoadMode === "deferred",
            }));
    }

    public async requestFullInstanceLoad(bindingPath: AbsoluteModelPath, ...instances: string[]): Promise<void> {
        return await this.requestInstancesLoadInternal(LazyLoadingInstanceState.FullyLoaded, bindingPath, ...instances);
    }

    public async requestFullInstanceLoadForCustomPath(mappedPath: AbsoluteModelPath): Promise<void> {
        const mappedPathTokens = mappedPath.getPathPartsAsArray();
        const instanceTokenIndices = Iterator.from(IterUtils.iterateEntries(mappedPath.getPathParts()))
            .filter(([_, item]) => PathTokens.isInstanceToken(item))
            .map(([index]) => index);
        for (const instanceTokenIndex of instanceTokenIndices) {
            const multiplicityItems = mappedPathTokens.slice(0, instanceTokenIndex);
            const multiplicityPath = createAbsoluteFromTokens(multiplicityItems);
            const instance = mappedPathTokens[instanceTokenIndex];
            if (instance != undefined && PathTokens.isInstanceToken(instance)) {
                await this.requestFullInstanceLoad(multiplicityPath, instance);
            }
        }
    }

    public setLazyLoadingEnabledMode(enabled: boolean) {
        this.lazyLoadingEnabled = enabled;
    }

    public isLazyLoadingEnabled(): boolean {
        return this.lazyLoadingEnabled;
    }

    public hasLazyLoadRules(): boolean {
        return !this.originalLazyLoadingDeclaration.isEmpty;
    }

    public async clearState(): Promise<void> {
        this.isInCleaningState = true;
        try {
            const promisesToAwait = Iterator.from(this.lazyLoadingElementInfo.values())
                .map(x =>
                    Iterator.from(x.instanceIds.values())
                        .map(child => child.loadingDeferred?.promise)
                        .filter(isNotNullOrUndefined)
                        .toArray()
                )
                .flatMap(x => x)
                .map(x => x?.catch(() => null))
                .toArray();

            await Promise.all(promisesToAwait);
            this.lazyLoadingElementInfo.clear();
        } finally {
            this.isInCleaningState = false;
        }
    }

    public async waitForAllPendingRequests(): Promise<void> {
        const allPendingRequests = Iterator.from(this.lazyLoadingElementInfo.values())
            .flatMap(x => x.instanceIds.values())
            .map(x => x.loadingDeferred)
            .filter(isNotNullOrUndefined)
            .toArray();
        await Promise.all(allPendingRequests);
    }
}
